import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import FadeLoader from "react-spinners/FadeLoader";
import FormikForm from "components/shared/FormikForm";
import {
  getMongoSQLSetting,
  deleteMongoSQLSetting,
} from "store/HistoricalData/actions";
import MoveDataMongoPop from "components/Modals/moveDataMongoPop/MoveDataMongoPop";
import moment from "moment";
import { toast } from "react-toastify";
import paginationFactory, {
  PaginationListStandalone,
  PaginationProvider,
} from "react-bootstrap-table2-paginator";
import { Row, Col } from "reactstrap";

const List = () => {
  const dispatch = useDispatch();

  const { loading, mongoSQLSetting, totalCount } = useSelector(
    ({ HistoricalData }) => ({
      loading: HistoricalData?.loading,
      mongoSQLSetting: HistoricalData?.mongoSQLSetting,
      totalCount: HistoricalData?.totalCount,
    }),
  );

  const { permissions } = useSelector(({ Auth }) => ({
    permissions: Auth?.user?.permissions || [],
  }));

  const canDelete = permissions.includes("delete_data_from_mongoDb");

  const [moveMongoPop, setMoveMongoPop] = useState(false);
  const [id, setId] = useState(null);
  const [selectedDates, setSelectedDates] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 20;

  const [filter, setFilter] = useState({
    page: 1,
    limit: 20,
    orderBy: "date",
    order: 1,
    startDate: "",
    endDate: "",
    status: "",
    isDataRemove: "false",
  });

  const initialValuesForFilter = {
    startDate: "",
    endDate: "",
    status: "",
    isDataRemove: "false",
  };

  const inputFieldsForSearch = [
    { name: "startDate", type: "date", label: "", placeholder: "Start Date" },
    { name: "endDate", type: "date", label: "", placeholder: "End Date" },
    {
      name: "isDataRemove",
      type: "select",
      label: "",
      placeholder: "Data Removed From Mongo",
      options: [
        { value: "", label: "All" },
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
    },
    {
      name: "status",
      type: "select",
      label: "",
      placeholder: "Select Status",
      options: [
        { value: "", label: "All" },
        { value: "success", label: "Success" },
        { value: "partial", label: "Partial" },
        { value: "failed", label: "Failed" },
        { value: "pending", label: "Pending" },
      ],
    },
  ];

  useEffect(() => {
    getApiData();
  }, [filter]);

  const getApiData = () => {
    try {
      const payload = {
        page: filter.page,
        limit: filter.limit,
        orderBy: filter.orderBy,
        order: filter.order,
        ...(filter.startDate && { startDate: filter.startDate }),
        ...(filter.endDate && { endDate: filter.endDate }),
        ...(filter.status && { status: filter.status }),
        ...(filter.isDataRemove !== "" &&
          filter.isDataRemove !== undefined && {
            isDataRemove: filter.isDataRemove === "true",
          }),
      };
      dispatch(getMongoSQLSetting({ data: payload }));
    } catch (error) {}
  };

  // ─── beforeDate from API response ─────────────────────────────────────────────
  const beforeDate = mongoSQLSetting?.beforeDate;

  // ─── Only rows BEFORE beforeDate are selectable (reversed logic) ──────────────
  const selectableRows = useMemo(
    () =>
      mongoSQLSetting?.historyList?.filter(
        (row) =>
          !row.isDataRemove &&
          row.status === "success" &&
          (!beforeDate || row.date < beforeDate), // ← only before beforeDate
      ) || [],
    [mongoSQLSetting, beforeDate],
  );

  const allSelected =
    selectableRows.length > 0 && selectedDates.length === selectableRows.length;

  const isIndeterminate =
    selectedDates.length > 0 && selectedDates.length < selectableRows.length;

  const handleSelectAll = () => {
    if (selectedDates.length === selectableRows.length) {
      setSelectedDates([]);
    } else {
      setSelectedDates(selectableRows.map((row) => row.date));
    }
  };

  const handleRowSelect = (date) => {
    setSelectedDates((prev) =>
      prev.includes(date) ? prev.filter((d) => d !== date) : [...prev, date],
    );
  };

  const handleViewHandler = (info) => {
    setMoveMongoPop(true);
    setId(info?._id);
  };

  const handleFilterForm = (info) => {
    setSelectedDates([]);
    setFilter((prev) => ({
      ...prev,
      page: 1,
      startDate: info.startDate || "",
      endDate: info.endDate || "",
      status: info.status || "",
      isDataRemove: info.isDataRemove ?? "false",
    }));
  };

  const [pageOptions, setPageOptions] = useState({
    sizePerPage: limit,
    totalSize: totalCount,
    custom: true,
    page: filter.page,
  });

  useEffect(() => {
    setPageOptions({
      sizePerPage: limit,
      totalSize: totalCount,
      custom: true,
      page: filter.page,
    });
  }, [totalCount, filter.page]);

  const handleDeleteSelected = () => setShowDeleteConfirm(true);

  // ─── confirmDelete — err is an Error object from the API helper ───────────────
  const confirmDelete = () => {
    dispatch(
      deleteMongoSQLSetting({
        data: { removeData: selectedDates },
        callback: (err, res) => {
          if (err) {
            // err.message is set by the API helper:
            // throw new Error(response?.data?.message || "Something went wrong!")
            toast.error(
              err?.message || "Something went wrong. Please try again.",
            );
            setShowDeleteConfirm(false);
            return;
          }
          // success
          toast.success("Data removed successfully.");
          setSelectedDates([]);
          setShowDeleteConfirm(false);
          getApiData();
        },
      }),
    );
  };

  // ─── Status badge ─────────────────────────────────────────────────────────────
  const getStatusBadge = (status) => {
    const map = {
      success: "badge badge-success",
      partial: "badge badge-warning",
      failed: "badge badge-danger",
      pending: "badge badge-secondary",
    };
    return status ? (
      <span className={map[status] || "badge badge-secondary"}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    ) : (
      "-"
    );
  };

  // ─── Custom checkbox component — fully controlled by React state ──────────────
  const CustomCheckbox = ({ checked, disabled, onChange, title }) => (
    <div
      style={{
        position: "relative",
        width: "20px",
        height: "20px",
        margin: "0 auto",
        cursor: disabled ? "not-allowed" : "pointer",
      }}
      title={title}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        style={{
          position: "absolute",
          opacity: 0,
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          margin: 0,
          cursor: disabled ? "not-allowed" : "pointer",
          zIndex: 9,
        }}
      />
      <div
        style={{
          width: "18px",
          height: "18px",
          border: "2px solid",
          borderColor: disabled ? "#dee2e6" : checked ? "#4f46e5" : "#adb5bd",
          borderRadius: "4px",
          backgroundColor: disabled ? "#f8f9fa" : checked ? "#4f46e5" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s ease",
          pointerEvents: "none",
        }}
      >
        {checked && !disabled && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path
              d="M1 4L4 7.5L10 1"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </div>
  );

  // ─── Header checkbox — also handles indeterminate ─────────────────────────────
  const HeaderCheckbox = () => (
    <div
      style={{
        position: "relative",
        width: "20px",
        height: "20px",
        margin: "0 auto",
        cursor: selectableRows.length === 0 ? "not-allowed" : "pointer",
      }}
    >
      <input
        type="checkbox"
        checked={allSelected}
        disabled={selectableRows.length === 0}
        onChange={handleSelectAll}
        ref={(el) => {
          if (el) el.indeterminate = isIndeterminate;
        }}
        style={{
          position: "absolute",
          opacity: 0,
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          margin: 0,
          cursor: selectableRows.length === 0 ? "not-allowed" : "pointer",
          zIndex: 9,
        }}
      />
      <div
        style={{
          width: "18px",
          height: "18px",
          border: "2px solid",
          borderColor:
            selectableRows.length === 0
              ? "#dee2e6"
              : allSelected || isIndeterminate
                ? "#4f46e5"
                : "#adb5bd",
          borderRadius: "4px",
          backgroundColor:
            selectableRows.length === 0
              ? "#f8f9fa"
              : allSelected || isIndeterminate
                ? "#4f46e5"
                : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s ease",
          pointerEvents: "none",
        }}
      >
        {allSelected && selectableRows.length > 0 && (
          <svg width="11" height="9" viewBox="0 0 11 9" fill="none">
            <path
              d="M1 4L4 7.5L10 1"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
        {isIndeterminate && !allSelected && (
          <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
            <path
              d="M1 1H9"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        )}
      </div>
    </div>
  );

  return (
    <>
      {loading && (
        <div className="main-loader">
          <FadeLoader size={1000} />
        </div>
      )}

      {moveMongoPop && (
        <MoveDataMongoPop
          show={moveMongoPop}
          handleClose={() => {
            setMoveMongoPop(false);
            setId(null);
          }}
          title={"Move Data Details"}
          id={id}
        />
      )}

      {showDeleteConfirm && (
        <div
          className="modal fade show d-block"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  className="close"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p>
                  Are you sure you want to remove data for{" "}
                  <strong>{selectedDates.length}</strong> record(s) from Mongo
                  DB?
                </p>
                <p className="text-muted" style={{ fontSize: "13px" }}>
                  Selected dates: <strong>{selectedDates.join(", ")}</strong>
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={confirmDelete}
                  disabled={loading}
                >
                  {loading ? "Deleting..." : "Confirm Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <section className="section">
        <div className="section-header">
          <h1>My Sql Backup Data List</h1>
        </div>
        <div className="section-body">
          <div className="card">
            <div className="row justify-content-between">
              <div className="col-12">
                <div className="card-header">
                  <FormikForm
                    initialValues={initialValuesForFilter}
                    onSubmit={handleFilterForm}
                    loading={loading}
                    inputFields={inputFieldsForSearch}
                    buttonText={"Search"}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ─── beforeDate Info Box — only rows BEFORE this date can be deleted ── */}
          {beforeDate && (
            <div
              className="alert d-flex align-items-center mb-3"
              style={{
                fontSize: "14px",
                backgroundColor: "#e8f5e9",
                border: "1px solid #a5d6a7",
                borderRadius: "6px",
                padding: "12px 16px",
                color: "#2e7d32",
              }}
            >
              <i
                className="fas fa-info-circle mr-2"
                style={{ fontSize: "16px", color: "#388e3c" }}
              />
              <span>
                Only records before{" "}
                <strong>{moment(beforeDate).format("DD MMM YYYY")}</strong> are
                eligible for deletion. Rows on or after this date cannot be
                selected.
              </span>
            </div>
          )}

          {canDelete && selectedDates.length > 0 && (
            <div className="mb-3 d-flex align-items-center justify-content-between px-1">
              <span className="text-muted" style={{ fontSize: "14px" }}>
                {selectedDates.length} row(s) selected
              </span>
              <button
                className="btn btn-danger btn-sm"
                onClick={handleDeleteSelected}
                disabled={loading}
              >
                <i className="fas fa-trash mr-1" />
                Delete Selected ({selectedDates.length})
              </button>
            </div>
          )}

          {!loading && mongoSQLSetting?.historyList?.length > 0 && (
            <div className="card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table table-centered table-nowrap">
                    <thead className="thead-light">
                      <tr>
                        {canDelete && (
                          <th
                            style={{
                              width: "50px",
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <HeaderCheckbox />
                          </th>
                        )}
                        <th>Date</th>
                        <th>Status</th>
                        <th>Created At</th>
                        <th>Data Removed from Mongo</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mongoSQLSetting?.historyList?.map((row) => {
                        const isSelected = selectedDates.includes(row.date);
                        const isRemoved = row.isDataRemove;
                        const isSuccess = row.status === "success";
                        // Row is restricted if it is ON or AFTER beforeDate
                        const isAfterAllowed =
                          beforeDate && row.date >= beforeDate;
                        const isDisabled =
                          isRemoved || !isSuccess || isAfterAllowed;

                        return (
                          <tr
                            key={row._id}
                            style={
                              isRemoved
                                ? {
                                    backgroundColor: "#f5f5f5",
                                    color: "#999",
                                    opacity: 0.75,
                                  }
                                : isAfterAllowed
                                  ? {
                                      backgroundColor: "#fafafa",
                                      color: "#bbb",
                                      opacity: 0.8,
                                    }
                                  : isSelected
                                    ? { backgroundColor: "#ede9fe" }
                                    : {}
                            }
                          >
                            {canDelete && (
                              <td
                                style={{
                                  textAlign: "center",
                                  verticalAlign: "middle",
                                }}
                              >
                                <CustomCheckbox
                                  checked={isSelected}
                                  disabled={isDisabled}
                                  onChange={() =>
                                    !isDisabled && handleRowSelect(row.date)
                                  }
                                  title={
                                    isRemoved
                                      ? "Data already removed"
                                      : isAfterAllowed
                                        ? `Only rows before ${moment(beforeDate).format("DD MMM YYYY")} can be deleted`
                                        : !isSuccess
                                          ? "Only success records can be deleted"
                                          : isSelected
                                            ? "Deselect row"
                                            : "Select row"
                                  }
                                />
                              </td>
                            )}
                            <td style={{ verticalAlign: "middle" }}>
                              {row.date
                                ? moment(row.date).format("DD MMM YYYY")
                                : "-"}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {getStatusBadge(row.status)}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {row.createdAt
                                ? moment(row.createdAt).format(
                                    "DD MMM YYYY, hh:mm A",
                                  )
                                : "-"}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              {isRemoved ? (
                                <span className="badge badge-dark">Yes</span>
                              ) : (
                                <span className="badge badge-success">No</span>
                              )}
                            </td>
                            <td style={{ verticalAlign: "middle" }}>
                              <button
                                className="fas fa-eye bg-white border-0"
                                title="view"
                                style={{ fontSize: "16px", cursor: "pointer" }}
                                onClick={() => handleViewHandler(row)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {totalCount > limit && (
                  <Row className="align-items-md-center mt-2 p-3">
                    <Col className="pagination pagination-rounded justify-content-end mb-2 inner-custom-pagination">
                      <ul className="pagination pagination-rounded mb-0">
                        {/* Previous */}
                        <li
                          className={`page-item ${filter.page === 1 ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setFilter((prev) => ({
                                ...prev,
                                page: prev.page - 1,
                              }))
                            }
                            disabled={filter.page === 1}
                          >
                            ‹
                          </button>
                        </li>

                        {/* Page numbers */}
                        {Array.from(
                          { length: Math.ceil(totalCount / limit) },
                          (_, i) => i + 1,
                        )
                          .filter((p) => {
                            const total = Math.ceil(totalCount / limit);
                            return (
                              p === 1 ||
                              p === total ||
                              Math.abs(p - filter.page) <= 2
                            );
                          })
                          .reduce((acc, p, idx, arr) => {
                            if (idx > 0 && p - arr[idx - 1] > 1) {
                              acc.push("...");
                            }
                            acc.push(p);
                            return acc;
                          }, [])
                          .map((p, idx) =>
                            p === "..." ? (
                              <li
                                key={`ellipsis-${idx}`}
                                className="page-item disabled"
                              >
                                <span className="page-link">…</span>
                              </li>
                            ) : (
                              <li
                                key={p}
                                className={`page-item ${filter.page === p ? "active" : ""}`}
                              >
                                <button
                                  className="page-link"
                                  onClick={() =>
                                    setFilter((prev) => ({ ...prev, page: p }))
                                  }
                                >
                                  {p}
                                </button>
                              </li>
                            ),
                          )}

                        {/* Next */}
                        <li
                          className={`page-item ${filter.page * limit >= totalCount ? "disabled" : ""}`}
                        >
                          <button
                            className="page-link"
                            onClick={() =>
                              setFilter((prev) => ({
                                ...prev,
                                page: prev.page + 1,
                              }))
                            }
                            disabled={filter.page * limit >= totalCount}
                          >
                            ›
                          </button>
                        </li>
                      </ul>
                    </Col>
                  </Row>
                )}
              </div>
            </div>
          )}

          {!loading && (!mongoSQLSetting?.historyList || totalCount === 0) && (
            <p style={{ textAlign: "center" }}>No Data Found</p>
          )}
        </div>
      </section>
    </>
  );
};

export default List;
